import { FlightsClassTypes } from 'constants/flight'
import useStableObjectReference from 'hooks/useStableObjectReference'
import { EmptyObject } from 'lib/object/objectUtils'
import { useMemo } from 'react'
import useCharterFlightJourneys, { extractDepartingAndReturningFaresForClass } from './useCharterFlightJourneys'

export function useFareClassPriceDifferencesPerAdult<C extends FlightsClassTypes>({
  charterBundleRoute, baseClass, comparisonClasses: _comparisonClasses, departDate, returnDate, occupancies, selectedFareClass,
}: {
  charterBundleRoute: App.CharterBundleRoute
  baseClass: FlightsClassTypes
  comparisonClasses: Array<C>
  departDate?: string
  returnDate?: string
  occupancies: Array<App.Occupants>
  selectedFareClass: FlightsClassTypes
}): Partial<Record<C, number>> {
  const comparisonClasses = useStableObjectReference(_comparisonClasses)

  const { fareFamilies } = useCharterFlightJourneys({
    charterBundleRoute,
    departDate,
    returnDate,
    occupancies,
    fareClass: selectedFareClass,
  })

  return useMemo(
    () => {
      if (!fareFamilies) {
        return EmptyObject as Partial<Record<C, number>>
      }

      const [baseDepartingFareFamily, baseReturningFareFamily] = extractDepartingAndReturningFaresForClass(fareFamilies, baseClass)
      const basePricePerAdult = (baseDepartingFareFamily && baseReturningFareFamily) ? baseDepartingFareFamily.cost.adult + baseReturningFareFamily.cost.adult : 0

      if (!basePricePerAdult) {
        return EmptyObject as Partial<Record<C, number>>
      }

      return comparisonClasses.reduce((differences, fareClass) => {
        const [departingFareFamily, returningFareFamily] = extractDepartingAndReturningFaresForClass(fareFamilies, fareClass)
        const pricePerAdult = (departingFareFamily && returningFareFamily) ? departingFareFamily.cost.adult + returningFareFamily.cost.adult : 0

        if (pricePerAdult) {
          differences[fareClass] = pricePerAdult - basePricePerAdult
        }

        return differences
      }, {} as Partial<Record<C, number>>)
    },
    [fareFamilies, baseClass, comparisonClasses],
  )
}
